import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Volume2, VolumeX, Loader2, Sparkles, CheckCircle2, PartyPopper, BookOpenText } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F"];
// Emojis that give away or duplicate answer state — never show these on option cards.
const BLOCKED_EMOJI = new Set(["❌", "✖", "✖️", "✗", "❎", "✅", "✔", "✔️", "☑", "☑️", "✓", "🚫", "⛔", "🔴", "🟢"]);
const cleanEmoji = (e) => {
  const t = (e || "").trim();
  return t && !BLOCKED_EMOJI.has(t) ? t : null;
};

export function ReadAlong({ data }) {
  const [busy, setBusy] = useState(null);
  const audioRef = useRef(null);

  const speak = async (text, key) => {
    setBusy(key);
    try {
      const res = await api.post("/tutor/tts", { text }, { responseType: "blob" });
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(URL.createObjectURL(res.data));
      audioRef.current = audio;
      audio.play();
    } catch {
      toast.error("Audio unavailable.");
    } finally {
      setBusy(null);
    }
  };

  const sentences = data.sentences || [];
  return (
    <section className="mt-10 rounded-3xl border-2 border-border bg-card p-6 shadow-sm sm:p-8" data-testid="read-along">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-head text-2xl font-black"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600"><BookOpenText className="h-5 w-5" strokeWidth={2.5} /></span> Read Along</h2>
        <button onClick={() => speak(sentences.join(" "), "all")} className="tactile flex items-center gap-2 rounded-full bg-sky-500 px-5 py-2.5 text-sm font-extrabold text-white" data-testid="read-along-all">
          {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-5 w-5" strokeWidth={2.5} />} Read All
        </button>
      </div>
      <ul className="mt-5 space-y-3">
        {sentences.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-3 rounded-2xl bg-muted p-4">
            <span className="text-xl font-bold leading-snug sm:text-2xl">{s}</span>
            <button onClick={() => speak(s, i)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-500 text-white transition-transform hover:scale-110" data-testid={`read-along-${i}`}>
              {busy === i ? <Loader2 className="h-5 w-5 animate-spin" /> : <Volume2 className="h-5 w-5" strokeWidth={2.5} />}
            </button>
          </li>
        ))}
      </ul>
      {data.practice_words?.length > 0 && (
        <div className="mt-6">
          <p className="label-caps text-muted-foreground">Practice Words</p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {data.practice_words.map((w, i) => (
              <button key={i} onClick={() => speak(w, `w${i}`)} className="rounded-full border-2 border-border bg-card px-4 py-2 text-base font-extrabold transition-colors hover:border-sky-400 hover:text-sky-600">{w}</button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function InteractiveLesson({ data }) {
  const questions = data.questions || [];
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const cm = data.character_messages || {};
  const encourage = cm.encouragement || ["Great job!"];

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };

  const speak = async (text) => {
    if (!text) return;
    // Toggle off if already speaking
    if (audioRef.current) { stopAudio(); return; }
    setSpeaking(true);
    try {
      const res = await api.post("/tutor/tts", { text }, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(res.data));
      audioRef.current = audio;
      audio.onended = () => { audioRef.current = null; setSpeaking(false); };
      await audio.play();
    } catch {
      audioRef.current = null;
      setSpeaking(false);
      toast.error("Audio unavailable.");
    }
  };

  const q = questions[idx];

  // Auto-read each new question aloud (best-effort; browsers may block until a tap).
  useEffect(() => {
    stopAudio();
    if (q?.instruction) {
      api.post("/tutor/tts", { text: q.instruction }, { responseType: "blob" })
        .then((res) => {
          const audio = new Audio(URL.createObjectURL(res.data));
          audioRef.current = audio;
          setSpeaking(true);
          audio.onended = () => { audioRef.current = null; setSpeaking(false); };
          return audio.play();
        })
        .catch(() => { audioRef.current = null; setSpeaking(false); });
    }
    return stopAudio;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, done]);

  if (questions.length === 0) return null;
  const progressPct = done ? 100 : Math.round((idx / questions.length) * 100);

  const choose = (opt, i) => {
    if (picked !== null) return;
    setPicked(i);
    stopAudio();
    if (opt.is_correct) {
      speak(encourage[idx % encourage.length]);
      setTimeout(() => {
        if (idx + 1 >= questions.length) setDone(true);
        else { setIdx(idx + 1); setPicked(null); }
      }, 1400);
    } else {
      speak("Not quite. Try again!");
      setTimeout(() => setPicked(null), 1400);
    }
  };

  return (
    <section className="mt-10 rounded-3xl border-2 border-border bg-card p-6 shadow-sm sm:p-8" data-testid="interactive-lesson">
      <h2 className="flex items-center gap-2 font-head text-2xl font-black"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 text-purple-600"><Sparkles className="h-5 w-5" strokeWidth={2.5} /></span> Play &amp; Learn</h2>
      {done ? (
        <div className="mt-6 flex flex-col items-center py-10 text-center" data-testid="interactive-complete">
          <div className="flex h-24 w-24 animate-pop items-center justify-center rounded-full bg-green-100 text-green-600"><PartyPopper className="h-12 w-12" strokeWidth={2.25} /></div>
          <p className="mt-5 max-w-md font-head text-2xl font-black">{cm.completion || "You did it! You finished the lesson!"}</p>
          <button onClick={() => { setIdx(0); setPicked(null); setDone(false); }} className="tactile mt-6 rounded-full bg-primary px-7 py-3 text-base font-extrabold text-white" data-testid="interactive-replay">Play Again</button>
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div className="mt-5 flex items-center gap-3">
            <div className="h-4 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-[#58CC02] transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="shrink-0 text-sm font-extrabold text-muted-foreground">{idx + 1}/{questions.length}</span>
          </div>

          {idx === 0 && cm.intro && <p className="mt-4 text-base font-bold text-muted-foreground">{cm.intro}</p>}
          <div className="mt-4 flex items-start gap-3">
            <button onClick={() => speak(q.instruction)} data-testid="interactive-listen"
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white transition-transform hover:scale-105 ${speaking ? "bg-red-500" : "bg-sky-500"}`}
              aria-label={speaking ? "Stop reading" : "Hear the question"}>
              {speaking ? <VolumeX className="h-6 w-6" strokeWidth={2.5} /> : <Volume2 className="h-6 w-6" strokeWidth={2.5} />}
            </button>
            <p className="font-head text-2xl font-black leading-tight">{q.instruction}</p>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {(q.options || []).map((opt, i) => {
              const isPicked = picked === i;
              const emoji = cleanEmoji(opt.emoji);
              let cls = "border-border bg-card hover:border-primary hover:-translate-y-1";
              let anim = "";
              if (picked !== null) {
                if (opt.is_correct) { cls = "border-green-500 bg-green-50 text-green-700"; anim = "animate-pop"; }
                else if (isPicked) { cls = "border-red-400 bg-red-50 text-red-600"; anim = "animate-shake"; }
                else cls = "border-border opacity-50";
              }
              return (
                <button key={i} onClick={() => choose(opt, i)} data-testid={`interactive-opt-${i}`} disabled={picked !== null}
                  className={`flex min-h-[128px] flex-col items-center justify-center gap-3 rounded-3xl border-4 p-5 text-center transition-all duration-200 ${cls} ${anim}`}>
                  {emoji ? (
                    <span className="text-5xl">{emoji}</span>
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 font-head text-2xl font-black text-primary">{LETTERS[i] || i + 1}</span>
                  )}
                  <span className="text-base font-extrabold leading-tight">{opt.label}</span>
                  {picked !== null && opt.is_correct && <CheckCircle2 className="h-6 w-6 text-green-500" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
          {picked !== null && (
            <p className={`mt-5 text-center text-lg font-extrabold ${q.options?.[picked]?.is_correct ? "text-green-600" : "text-red-500"}`} data-testid="interactive-feedback">
              {q.options?.[picked]?.is_correct ? encourage[idx % encourage.length] : "Not quite — try again! 💪"}
            </p>
          )}
        </>
      )}
    </section>
  );
}
